import type { KeyFilters } from '../types/filters';
import type { ResourceLibraryItemBase } from '../types/resourceLibraryItems';
import type { ProductGroup } from '../types/ProductGroup';
import type { ProductApplication } from '../types/ProductApplication';
import type { NavigationNode } from '../../siteNavigation/types/NavigationNode';
import type { Brand } from '../types/Brand';

type HierarchicalNode = { key: string; parent?: HierarchicalNode };

const buildHierarchicalKey = (node?: HierarchicalNode): string | undefined => {
    if (!node) {
        return undefined;
    }

    const segments: string[] = [];
    let current: HierarchicalNode | undefined = node;
    while (current) {
        segments.push(current.key);
        current = current.parent;
    }

    return segments.reverse().join('/');
};

const collectHierarchicalKeys = <T extends HierarchicalNode>(
    ids: readonly string[],
    dictionary?: Record<string, T>
): string[] | undefined => {
    if (!dictionary || ids.length === 0) {
        return undefined;
    }

    const keys = ids
        .map((id) => buildHierarchicalKey(dictionary[id]))
        .filter((key): key is string => Boolean(key));

    if (keys.length === 0) {
        return undefined;
    }

    return Array.from(new Set(keys));
};

const collectPaths = (ids: readonly string[], dictionary?: Record<string, NavigationNode>) => {
    if (!dictionary || ids.length === 0) {
        return undefined;
    }

    const paths = ids
        .map((id) => dictionary[id]?.path ?? null)
        .filter((path): path is string => Boolean(path));

    if (paths.length === 0) {
        return undefined;
    }

    return Array.from(new Set(paths));
};

export interface BuildKeyFiltersResolvers {
    brandById?: Record<string, Pick<Brand, 'key'>>;
    productGroupById?: Record<string, ProductGroup>;
    productApplicationById?: Record<string, ProductApplication>;
    navigationNodeById?: Record<string, NavigationNode>;
}

export const buildKeyFiltersFromItem = (
    item: Pick<
        ResourceLibraryItemBase,
        | 'brandId'
        | 'productGroupIds'
        | 'productApplicationIds'
        | 'navigationScopeIds'
        | 'exclusiveNavigationScopeIds'
    >,
    resolvers: BuildKeyFiltersResolvers
): KeyFilters => {
    const filters: KeyFilters = {};

    const { brandById, productGroupById, productApplicationById, navigationNodeById } = resolvers;

    if (item.brandId && brandById) {
        const brand = brandById[item.brandId];
        if (brand?.key) {
            filters.brandKey = brand.key;
        }
    }

    const productGroupKeys = collectHierarchicalKeys(item.productGroupIds, productGroupById);
    if (productGroupKeys) {
        filters.productGroupKeys = productGroupKeys;
    }

    const productApplicationKeys = collectHierarchicalKeys(
        item.productApplicationIds,
        productApplicationById
    );
    if (productApplicationKeys) {
        filters.productApplicationKeys = productApplicationKeys;
    }

    const navigationScopePaths = collectPaths(item.navigationScopeIds, navigationNodeById);
    if (navigationScopePaths) {
        filters.navigationScopePaths = navigationScopePaths;
    }

    const exclusiveNavigationScopePaths = collectPaths(
        item.exclusiveNavigationScopeIds,
        navigationNodeById
    );
    if (exclusiveNavigationScopePaths) {
        filters.exclusiveNavigationScopePaths = exclusiveNavigationScopePaths;
    }

    return filters;
};

export const pickKeyFilters = (
    filters: KeyFilters,
    keys: readonly (keyof KeyFilters)[]
): KeyFilters => {
    const picked: KeyFilters = {};

    keys.forEach((key) => {
        const value = filters[key];
        if (Array.isArray(value)) {
            if (value.length > 0) {
                (picked as Record<string, unknown>)[key as string] = value.slice();
            }
        } else if (value !== undefined && value !== null) {
            (picked as Record<string, unknown>)[key as string] = value;
        }
    });

    return picked;
};
